import { useCallback, useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { ApiError } from '../api/client';
import { eventsApi } from '../api/events';
import type { EventDetail } from '../api/events';
import { gearApi } from '../api/gear';
import type { GearException, GearItem, GearRegister } from '../api/gear';
import type { Me } from '../api/auth';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { DISCLAIMER, Footer, Gutter, Page } from '../ui/Chrome';
import { Tabs } from '../ui/Tabs';
import { formatInZone } from '../lib/datetime';

/**
 * The troop's gear on loan: the sheet that goes out, the sheet that comes back, and the one place
 * a leader turns the marked-up paper into state. TROOP-167 / 172 / 173 / 174.
 *
 * ---
 *
 * **Item per row, not person per row — and therefore not `PeopleGrid`.** `PeopleGrid` is the one
 * definition of a person-per-row table and `CLAUDE.md` is explicit that "a bespoke table inside a
 * panel because it is only a small one is the bug". This is not that: the subject here is an
 * **item**, and one person may hold four of them. Reusing `PeopleGrid` would mean a column per item
 * and a row per person, which is the wrong shape for a sheet somebody works down at a trailer.
 *
 * There is a second, concrete reason. `.pg-print-only`'s base `display: none` lives only inside
 * `EventRosterScreen`'s `PrintStyles` — deliberately, so a shared component's print twins are
 * opt-in. A second screen passing `paper` would have to re-declare every `.pg-*` print rule, and
 * the two copies would drift. A different subject with its own table avoids that entirely, and no
 * `.pg-*` class appears on this screen.
 *
 * **Why check-in is paper, stated so nobody optimises it away.** It happens at the trailer on a
 * Sunday afternoon, with tired Scouts, in the rain, unloading. A screen asking eleven people to
 * each tap something is a workflow that will not survive one real campout — and a leader chasing
 * them to do it is worse than the clipboard it replaced. So the product prints the sheet and gets
 * out of the way; recording the outcome afterwards is one leader, once, on the third tab. A future
 * contributor will at some point propose a per-Scout check-in screen. This paragraph is why the
 * answer is no.
 *
 * **The print rules are copied from `EventRosterScreen`, and three parts are load-bearing** — see
 * `PrintStyles` below.
 */
export function GearSheetsScreen({
  unitId,
  eventId,
  me,
  onNavigate,
}: {
  unitId: string;
  eventId: string;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [register, setRegister] = useState<GearRegister | null>(null);
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [sheet, setSheet] = useState('out');
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setError(null);

    try {
      const [rows, detail] = await Promise.all([
        gearApi.register(unitId, eventId),
        eventsApi.detail(unitId, eventId).catch(() => null),
      ]);

      setRegister(rows);
      setEvent(detail);
    } catch (e) {
      // The server is the enforcement point and answers 404 to "not allowed" as well as to "no
      // such trip", so the screen says what the rule is rather than guessing which one it was.
      if (e instanceof ApiError && e.status === 404) {
        setRefused(true);
      } else {
        setError(e instanceof Error ? e.message : 'Could not load the gear.');
      }
    }
  }, [unitId, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Ctrl+P has to produce the same paper the button does, so the bound is lifted on the event
  // rather than inside the click handler. Without this a leader printing from the browser menu
  // would get the first forty lines of a long trip and no sign that the rest existed.
  useEffect(() => {
    const unbound = () => setShowAll(true);
    window.addEventListener('beforeprint', unbound);
    return () => window.removeEventListener('beforeprint', unbound);
  }, []);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const shell = (body: React.ReactNode) => (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />
      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>{body}</div>
        </Gutter>
      </section>
      <Footer />
    </Page>
  );

  if (refused) {
    return shell(
      // Position, not classification: a youth Quartermaster lands on the sheets and a plain member
      // does not. The sentence names the rule rather than implying a permission somebody could
      // grant them.
      <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        What the unit has lent out is visible to its officers. Ask one of your unit&rsquo;s
        leaders.
      </p>,
    );
  }

  if (!register) {
    return shell(
      <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        {error ?? 'Loading the gear...'}
      </p>,
    );
  }

  const groups = groupByHolder(register.items);

  return shell(
    <div className="gs-root" style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PrintStyles />

      {/*
        The trip names the sheet, not the feature -- a stack of sheets from three campouts has to be
        tellable apart, and what identifies one is where everyone went. The dates sit underneath,
        in the event's own zone.
      */}
      <div className="gs-head">
        <h1 className="t-heading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          {event ? `${event.title} gear` : 'Trip gear'}
        </h1>
        {event && (
          <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            {formatInZone(event.startsAt, event.timeZoneId)}
            {' – '}
            {formatInZone(event.endsAt, event.timeZoneId)}
            {event.locationName && <> &middot; {event.locationName}</>}
          </p>
        )}
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {register.total} {register.total === 1 ? 'thing' : 'things'} of the unit&rsquo;s
          {register.outstanding > 0 && ` · ${register.outstanding} still out`}
        </p>
      </div>

      {error && (
        <p
          className="t-caption gs-screen-only"
          role="alert"
          style={{ color: 'var(--color-status-blocked)', margin: 0 }}
        >
          {error}
        </p>
      )}

      {register.total === 0 ? (
        <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Nothing on this trip is marked as the unit&rsquo;s yet. Tick the unit&rsquo;s own things
          on the packing lists and they will appear here.
        </p>
      ) : (
        <>
          <div className="gs-screen-only">
            <Button
              variant="secondary"
              onClick={() => {
                // Everything, not the first forty. The state change has to land before the print
                // dialog reads the page, so the call waits a frame.
                setShowAll(true);
                requestAnimationFrame(() => window.print());
              }}
            >
              <Printer size={16} aria-hidden />
              Print this sheet
            </Button>
          </div>

          <Tabs
            label="Which sheet"
            selectedId={sheet}
            onSelect={setSheet}
            tabs={[
              {
                id: 'out',
                label: 'Going out',
                panel: (
                  <Sheet
                    kind="out"
                    groups={groups}
                    showAll={showAll}
                    onShowAll={() => setShowAll(true)}
                  />
                ),
              },
              {
                id: 'in',
                label: 'Coming back',
                panel: (
                  <Sheet
                    kind="in"
                    groups={groups}
                    showAll={showAll}
                    onShowAll={() => setShowAll(true)}
                  />
                ),
              },
              {
                id: 'close',
                label: 'Close out',
                panel: (
                  <CloseOut
                    unitId={unitId}
                    eventId={eventId}
                    items={register.items}
                    busy={busy}
                    onRun={run}
                  />
                ),
              },
            ]}
          />
        </>
      )}

      {/* The Footer is hidden on paper, so the sheet carries its own copy. */}
      <p className="gs-print-only t-caption" style={{ marginTop: 'var(--space-4)' }}>
        {DISCLAIMER}
      </p>
    </div>,
  );
}

interface HolderGroup {
  key: string;
  name: string | null;
  items: GearItem[];
}

/**
 * One person's four things together, because that is how a handover actually happens. The
 * unclaimed lines sort last — the server already orders this way, so this only has to preserve it.
 */
function groupByHolder(items: GearItem[]): HolderGroup[] {
  const groups: HolderGroup[] = [];

  for (const item of items) {
    const key = item.holderPersonId ?? '';
    const existing = groups.find((g) => g.key === key);

    if (existing) {
      existing.items.push(item);
    } else {
      groups.push({ key, name: item.holderName, items: [item] });
    }
  }

  return groups;
}

/** How many rows are drawn before the bound bites. Lifted for print, and by the button. */
const DRAWN = 40;

/**
 * Both sheets, because a quartermaster working down two of them should not have to learn two
 * layouts. The only difference is the last column.
 */
function Sheet({
  kind,
  groups,
  showAll,
  onShowAll,
}: {
  kind: 'out' | 'in';
  groups: HolderGroup[];
  showAll: boolean;
  onShowAll: () => void;
}) {
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  let drawn = 0;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        {kind === 'out'
          ? 'Print this before you go. Hand each thing over and get an initial.'
          : 'Print this for the trailer. Tick what comes back and write down anything wrong with it.'}
      </p>

      <div className="gs-scroll" style={{ overflowX: 'auto' }}>
        <table
          className="gs-table"
          style={{ borderCollapse: 'collapse', width: '100%', minWidth: '34rem' }}
        >
          <thead>
            <tr>
              <Th>Who has it</Th>
              <Th>What</Th>
              <Th>Where it is from</Th>
              <Th>{kind === 'out' ? 'Initial' : 'Back?'}</Th>
              <Th>{kind === 'out' ? 'Notes' : 'Condition'}</Th>
            </tr>
          </thead>

          <tbody>
            {groups.map((group) =>
              group.items.map((item, index) => {
                if (!showAll && drawn >= DRAWN) return null;
                drawn += 1;

                return (
                  <tr key={item.itemId}>
                    <Td>
                      {/* The name once per run, so one person's four things read as one handover
                          rather than as four. */}
                      {index === 0 ? (group.name ?? <Muted>Nobody yet</Muted>) : ''}
                    </Td>
                    <Td>
                      {item.text}
                      {item.quantity > 1 && ` ×${item.quantity}`}
                    </Td>
                    <Td>
                      <span className="t-caption">
                        {item.subgroupName ?? (item.level === 'Personal' ? 'Own kit' : 'Unit')}
                      </span>
                    </Td>
                    <Td>
                      {/*
                        An empty ballot box, not a native checkbox. PeopleGrid learned this: a form
                        control is something the print stylesheet has just forced to black, and
                        browsers vary on whether its tick survives that at all; a glyph is text and
                        prints like the rest of the table.

                        And an item nobody was given renders an em-dash instead. An unticked box
                        reads as "not returned yet", which is the wrong thing to say about something
                        that never went out -- PeopleGrid's rule, transferring exactly.
                      */}
                      {item.holderPersonId ? (
                        kind === 'out' ? (
                          <Rule />
                        ) : (
                          <span aria-hidden="true" style={{ fontSize: '1.1em' }}>
                            &#9744;
                          </span>
                        )
                      ) : (
                        <Muted>&mdash;</Muted>
                      )}
                    </Td>
                    <Td>
                      {/* The field that exists only on paper: the condition, in whatever words the
                          person holding the pen uses. */}
                      <Rule />
                    </Td>
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>

      {!showAll && total > DRAWN && (
        <p className="gs-screen-only" style={{ margin: 0 }}>
          <button type="button" className="link-button t-body" onClick={onShowAll}>
            Show all {total} things
          </button>
        </p>
      )}
    </div>
  );
}

/**
 * The leader comes back from the trailer holding the marked-up sheet, and this is where it becomes
 * state — TROOP-174.
 *
 * **Exception-based, and that is the whole design.** A campout might carry thirty troop-owned
 * lines, and a close-out that asks for thirty confirmations is one that gets done once and never
 * again — at which point the register starts lying, which is worse than not having it. So the
 * leader marks only what is wrong and taps done; everything untouched is recorded as returned by
 * the server.
 *
 * **The control is a select, never an icon.** A cell here holds both a state and the way to change
 * it, and TROOP-135 is what happens when the icon speaks for the wrong one.
 */
function CloseOut({
  unitId,
  eventId,
  items,
  busy,
  onRun,
}: {
  unitId: string;
  eventId: string;
  items: GearItem[];
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => Promise<void>;
}) {
  // Only what the leader has touched this sitting. An untouched row with nothing recorded is
  // silence, and silence means "it came back" -- which is the one-tap case.
  const [marked, setMarked] = useState<Record<string, GearException>>({});

  /*
    What the button will actually send, which is not the same as what the leader typed.

    A problem already on record carries forward unless it is changed. Sending only `marked` would
    mean a leader opening this a week later, seeing "Axe · Missing" on screen, tapping the button
    without touching anything, and silently marking the axe returned -- the screen showing one
    thing and the control doing another, which is TROOP-135's failure wearing a different costume.
    Something already recorded as Returned is not carried, because that is what silence means
    anyway.
  */
  const effective = items
    .map((item): GearException | null => {
      if (marked[item.itemId]) return marked[item.itemId];

      return item.returned && item.returned !== 'Returned'
        ? {
            itemId: item.itemId,
            state: item.returned as GearException['state'],
            note: item.returnNote,
          }
        : null;
    })
    .filter((e): e is GearException => e !== null);

  const problems = effective.filter((e) => e.state !== 'Returned');

  return (
    <div className="gs-screen-only" style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Mark anything that did not simply come back. Everything you leave alone is recorded as
        returned.
      </p>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.map((item) => {
          const current = marked[item.itemId]?.state ?? item.returned ?? 'Returned';
          const note = marked[item.itemId]?.note ?? item.returnNote ?? '';

          return (
            <li
              key={item.itemId}
              className="flex flex-wrap items-center gap-2"
              style={{
                paddingBlock: 'var(--space-2)',
                borderTop: '1px solid var(--color-border-subtle)',
              }}
            >
              <span
                className="t-body"
                style={{ color: 'var(--color-text-primary)', flex: '1 1 10rem' }}
              >
                {item.text}
                <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
                  {' '}
                  &middot; {item.holderName ?? 'nobody yet'}
                </span>
              </span>

              <label className="inline-flex items-center gap-2">
                <span className="sr-only-focusable t-caption">How {item.text} came back</span>
                <select
                  value={current}
                  disabled={busy}
                  onChange={(e) =>
                    setMarked((was) => ({
                      ...was,
                      [item.itemId]: {
                        itemId: item.itemId,
                        state: e.target.value as GearException['state'],
                        note: note || null,
                      },
                    }))
                  }
                  style={{
                    minHeight: 'var(--tap-min)',
                    borderRadius: 'var(--radius-md)',
                    border: '1.5px solid var(--color-border-strong)',
                    background: 'var(--color-surface-raised)',
                    color: 'var(--color-text-primary)',
                    padding: '0 var(--space-2)',
                  }}
                >
                  <option value="Returned">Came back</option>
                  <option value="Damaged">Damaged</option>
                  <option value="Missing">Missing</option>
                </select>
              </label>

              {current !== 'Returned' && (
                <label className="inline-flex items-center gap-2" style={{ flex: '1 1 12rem' }}>
                  <span className="sr-only-focusable t-caption">What is wrong with {item.text}</span>
                  <input
                    type="text"
                    value={note}
                    disabled={busy}
                    placeholder="Zip broken"
                    onChange={(e) =>
                      setMarked((was) => ({
                        ...was,
                        [item.itemId]: {
                          itemId: item.itemId,
                          state: current as GearException['state'],
                          // Not trimmed here: a controlled box that trims in its own onChange eats
                          // the space before the next character arrives, and the space bar reads as
                          // dead. Trimming belongs at the boundary, once.
                          note: e.target.value || null,
                        },
                      }))
                    }
                    style={{
                      minHeight: 'var(--tap-min)',
                      width: '100%',
                      borderRadius: 'var(--radius-md)',
                      border: '1.5px solid var(--color-border-strong)',
                      background: 'var(--color-surface-raised)',
                      color: 'var(--color-text-primary)',
                      padding: '0 var(--space-2)',
                    }}
                  />
                </label>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center" style={{ gap: 'var(--space-3)' }}>
        <Button
          variant="primary"
          disabledReason={busy ? 'Saving what you marked' : undefined}
          onClick={() =>
            onRun(async () => {
              await gearApi.closeOut(unitId, eventId, effective);
              setMarked({});
            })
          }
        >
          {problems.length === 0
            ? 'Everything came back'
            : `Close out · ${problems.length} ${problems.length === 1 ? 'problem' : 'problems'}`}
        </Button>

        <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
          You can come back and correct any of this later.
        </span>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="t-label"
      style={{
        textAlign: 'left',
        padding: 'var(--space-2)',
        borderBottom: '1px solid var(--color-border-strong)',
        color: 'var(--color-text-secondary)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td
      className="t-body"
      style={{
        padding: 'var(--space-2)',
        borderBottom: '1px solid var(--color-border-subtle)',
        color: 'var(--color-text-primary)',
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span style={{ color: 'var(--color-text-muted)' }}>{children}</span>;
}

/** A blank wide enough to write on. On screen it is a hairline; on paper it is the field. */
function Rule() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        minWidth: '6rem',
        borderBottom: '1px solid var(--color-border-strong)',
      }}
    >
      &nbsp;
    </span>
  );
}

/**
 * SPEC-GAP: `handoff/design-system.md` describes no print output, so this is built from tokens only
 * and is declared in the component rather than in `app.css` — a global print stylesheet would be a
 * promise about every other page that nothing keeps.
 *
 * Three things here are load-bearing, all three copied from `EventRosterScreen`:
 *
 * 1. **The isolation is `visibility`, not `display`.** Hiding the chrome by tag name means knowing
 *    what `AppHeader` and `Footer` render, and breaks the day either changes. It also leaves the
 *    layout intact, so the table still measures.
 * 2. **Colours forced to black.** The tokens are a screen palette; a hairline in
 *    `--color-border-subtle` prints as nothing on a mono printer, and a ruled blank that is not
 *    ruled defeats the page.
 * 3. **The row bound is lifted on `beforeprint`** as well as in the button, so Ctrl+P produces the
 *    same paper.
 *
 * Its own `gs-` prefix on every rule, and no `.pg-*` class anywhere: `PeopleGrid`'s print twins are
 * opt-in and belong to the trip summary.
 *
 * **No backticks inside this template literal, prose comments included.** A backtick closes the
 * template and fails as about forty JSX parse errors starting a hundred lines below the cause.
 */
function PrintStyles() {
  return (
    <style>{`
      .gs-print-only { display: none; }

      @media print {
        /* Reveal only the sheet. See the remarks for why this is visibility. */
        body * { visibility: hidden; }
        .gs-root, .gs-root * { visibility: visible; }
        .gs-root {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }

        .gs-screen-only { display: none !important; }
        .gs-print-only { display: inline; }

        /* The tab bar is a control. On paper there is one sheet, and it is the one on screen. */
        [role="tablist"] { display: none !important; }

        /* Ink, not tokens. A hairline in a subtle grey prints as nothing, and a blank nobody can
           see is not a blank. */
        .gs-root, .gs-root * {
          color: #000 !important;
          background: transparent !important;
          box-shadow: none !important;
          border-color: #000 !important;
        }

        /* The container scrolls on screen; on paper it must let the table wrap onto the page. */
        .gs-scroll { overflow: visible !important; }

        .gs-table { width: 100%; font-size: 10pt; min-width: 0 !important; }
        .gs-table th, .gs-table td {
          border: 1px solid #999 !important;
          padding: 6px !important;
          vertical-align: top;
          white-space: normal !important;
        }
        .gs-table th { text-align: left; font-weight: 600; }

        /* A long trip runs over pages, so the heading row repeats and a line is not split in two. */
        .gs-table thead { display: table-header-group; }
        .gs-table tr { break-inside: avoid; }

        /* Sizes in points, because the type tokens are a screen scale and resolve to whatever the
           viewport was when the dialog opened. The trip's name is the largest thing on the sheet,
           which is what tells three campouts' sheets apart in a pile. */
        .gs-head h1 { font-size: 16pt; line-height: 1.2; }
        .gs-head p { font-size: 9pt; }
      }
    `}</style>
  );
}
